import type { Workbook, Worksheet } from "exceljs";
import { getPrimaryJob } from "../../../utils/jobs";
import { type CalledOffShift, DailyPrintoutGroupingOption, type HospitalData } from "../../../utils/models";
import { MAX_GROUP_MERGE_DIFF_MIN } from "../../../utils/timeGrouping";
import { groupKeyFor } from "../group/groupKey";
import { renderAdminsBlock } from "../render/adminsBlock";
import { renderCallOutsSection } from "../render/callOutsSection";
import { computeColumnWidths } from "../render/columnWidths";
import { assertAppendEmptyColumns } from "../render/emptyColumns";
import { applyPageSettings } from "../render/pageSettings";
import { renderPtoSection } from "../render/ptoSection";
import { renderSection } from "../render/section";
import { renderSheetHeader } from "../render/sheetHeader";
import type { LaidOutSheetPlan, NurseInShiftWithPto, PipelineContext, RenderSettings, RowNoteFlags, ScheduleRow } from "../types";
import { groupingKeyToDisplayName } from "../util/groupingDisplay";

/**
 * Filter the global PTO list down to only the shifts that belong on a given sheet (mapping §7.x).
 *
 * Matches legacy behavior:
 * - `NONE` — keep all PTO (single-sheet printout).
 * - `START_TIME` — proximity match (±MAX_GROUP_MERGE_DIFF_MIN) on both start AND end against the
 *   sheet's first row's shift window. Mirrors the regular-shift merge tolerance so PTO landing
 *   near a tab's anchor doesn't get orphaned to a different tab.
 * - `LOCATION` / `ROLE` / `FLOOR` — exact `groupKeyFor` match between the PTO's synthetic
 *   `ScheduleRow` and the sheet's raw key.
 * - `CUSTOM` — caller suppresses PTO entirely (this filter is not invoked).
 */
const filterPtoForSheet = (ptoShifts: NurseInShiftWithPto[], sheet: LaidOutSheetPlan, jobManagementEnabled: boolean): NurseInShiftWithPto[] => {
	if (sheet.splitBy === DailyPrintoutGroupingOption.NONE) return ptoShifts;

	if (sheet.splitBy === DailyPrintoutGroupingOption.START_TIME) {
		const firstTabRow = sheet.sections.find((s) => s.rows.length > 0)?.rows[0];
		if (!firstTabRow) return [];
		return ptoShifts.filter(
			(shift) =>
				Math.abs(shift.startTime.diff(firstTabRow.shiftStart, "minutes").minutes) <= MAX_GROUP_MERGE_DIFF_MIN &&
				Math.abs(shift.endTime.diff(firstTabRow.shiftEnd, "minutes").minutes) <= MAX_GROUP_MERGE_DIFF_MIN,
		);
	}

	// LOCATION / ROLE / FLOOR — exact groupKeyFor match against the sheet's raw key.
	return ptoShifts.filter((shift) => {
		const ptoRow: ScheduleRow = {
			location: shift.location || "",
			section: "",
			nurse: shift,
			role: shift.role,
			startHour: shift.startTime.hour,
			shiftKey: "",
			shiftStart: shift.startTime,
			shiftEnd: shift.endTime,
			floor: undefined,
		};
		return groupKeyFor({ row: ptoRow, groupingOption: sheet.splitBy, jobManagementEnabled }) === sheet.rawSheetKey;
	});
};

/**
 * Filter the global call-outs list down to only the call-outs that belong on a given sheet.
 *
 * Unlike `filterPtoForSheet`, this never suppresses the section entirely — there is no `CUSTOM`-style
 * exclusion for call-outs. Only two split kinds can genuinely be attributed to a specific sheet:
 * - `START_TIME` — same proximity match (±MAX_GROUP_MERGE_DIFF_MIN) `filterPtoForSheet` uses.
 * - `ROLE` — match via the resolved job/role key. `CalledOffShift` carries no role/job of its own
 *   ("the shift is gone" — see `MobileCallInItem.tsx`), so it's resolved client-side from the current
 *   nurse roster, matching `groupKeyFor`'s own ROLE-case sentinels (`"No Job"` / `"No Role"`) so the
 *   comparison against `sheet.rawSheetKey` lines up.
 *
 * Every other split kind — `NONE`, `LOCATION`, `FLOOR`, and `CUSTOM` — has no field on `CalledOffShift`
 * that could attribute a call-out to one specific sheet, so the full, unfiltered list is kept for all
 * of them: the same complete call-outs section is appended to every sheet rather than hidden or
 * arbitrarily scoped. A call-out can therefore appear repeated across location/floor/custom tabs — a
 * deliberate tradeoff, since showing the data everywhere is preferable to guessing which tab it
 * belongs to or hiding it.
 */
export const filterCallOutsForSheet = (
	callOutShifts: CalledOffShift[],
	sheet: LaidOutSheetPlan,
	hospitalData: HospitalData,
	jobManagementEnabled: boolean,
): CalledOffShift[] => {
	if (sheet.splitBy === DailyPrintoutGroupingOption.START_TIME) {
		const firstTabRow = sheet.sections.find((s) => s.rows.length > 0)?.rows[0];
		if (!firstTabRow) return [];
		return callOutShifts.filter(
			(callOut) =>
				Math.abs(callOut.startTime.diff(firstTabRow.shiftStart, "minutes").minutes) <= MAX_GROUP_MERGE_DIFF_MIN &&
				Math.abs(callOut.endTime.diff(firstTabRow.shiftEnd, "minutes").minutes) <= MAX_GROUP_MERGE_DIFF_MIN,
		);
	}

	if (sheet.splitBy === DailyPrintoutGroupingOption.ROLE) {
		return callOutShifts.filter((callOut) => {
			const nurse = hospitalData.nurses.find((n) => n.id === callOut.nurseId);
			const jobOrRoleKey = jobManagementEnabled ? getPrimaryJob(nurse)?.id || "No Job" : nurse?.role || "No Role";
			return jobOrRoleKey === sheet.rawSheetKey;
		});
	}

	return callOutShifts;
};

export interface RenderSheetsArgs {
	workbook: Workbook;
	sheets: LaidOutSheetPlan[];
	ptoShifts: NurseInShiftWithPto[];
	callOutShifts: CalledOffShift[];
	context: PipelineContext;
	settings: RenderSettings;
}

/**
 * Compose the `RowNoteFlags` consumed by the renderer.
 *
 * Both flags default to **on** when undefined (mapping §11.6).
 */
export const buildNoteFlags = (flagAgency: boolean | undefined, flagNotOnboarded: boolean | undefined): RowNoteFlags => ({
	includeAgency: flagAgency !== false,
	includeNotOnboarded: flagNotOnboarded !== false,
});

/**
 * Phase 6 — render each `LaidOutSheetPlan` into the workbook (mapping §4 phase 6 / §9.*).
 *
 * Per sheet:
 * 1. Add a worksheet with `sheet.sheetName` (already finalized — dedup + Excel sanitization
 *    happened in phase 4)
 * 2. Set column widths from the layout's columns + `appendEmptyColumns` blank ones + `printoutColumnWidths` overrides
 * 3. Apply A4 print settings (orientation auto-detected if not provided)
 * 4. Emit the sheet-header block (title, date, census, optional HPPD/nursing-hours)
 * 5. For each section, call `renderSection` — which handles standard vs. pivot dispatch internally
 * 6. Emit the PTO section if there are PTO shifts AND the sheet isn't from a custom-tab split
 *    (mapping §7.3 — PTO is suppressed under CUSTOM mode)
 * 7. Emit the Call Outs section if there are call-outs for this sheet (never suppressed by split
 *    mode — see `filterCallOutsForSheet`)
 *
 * `hideColumnNames` reaches every table (staff sections, PTO, Call Outs); `appendEmptyColumns` only the
 * staff sections. The sheet header and admins block span the appended columns because they read the
 * sheet width back from `worksheet.columns`.
 *
 * Cell wrapping and row heights are NOT done here: `createDailySchedule` applies `applyWrapText` to
 * every finished sheet, which covers the summary and Lowry renderers too.
 */
export const renderSheets = async (args: RenderSheetsArgs): Promise<void> => {
	const { workbook, sheets, ptoShifts, callOutShifts, context, settings } = args;
	const noteFlags = buildNoteFlags(settings.flagAgency, settings.flagNotOnboarded);
	assertAppendEmptyColumns(settings.appendEmptyColumns);

	for (const sheet of sheets) {
		const worksheet: Worksheet = workbook.addWorksheet(sheet.sheetName);

		const sheetColumns = sheet.layout.kind === "pivot" ? sheet.layout.flatColumns : sheet.layout.expandedColumns;
		// In pivot mode the layout already accounts for every column; `printNotes` doesn't get an
		// extra trailing column (preserving today's behavior).
		const effectivePrintNotes = sheet.layout.kind === "pivot" ? false : settings.printNotes;

		worksheet.columns = computeColumnWidths({
			enabledColumns: sheetColumns,
			printNotes: effectivePrintNotes,
			appendEmptyColumns: settings.appendEmptyColumns,
			columnWidthOverrides: settings.printoutColumnWidths,
		});

		const sheetDisplayName = groupingKeyToDisplayName(sheet.rawSheetKey, sheet.splitBy, context.jobManagementEnabled, context.department);
		const sheetTitle = sheet.splitBy === DailyPrintoutGroupingOption.NONE ? context.title : `${context.title} - ${sheetDisplayName}`;

		applyPageSettings({
			worksheet,
			enabledColumns: sheetColumns,
			printNotes: effectivePrintNotes,
			title: sheetTitle,
			date: context.date,
			orientation: settings.orientation,
			fitToPage: settings.fitToPage,
			fitToWidth: settings.fitToWidth,
			fitToHeight: settings.fitToHeight,
		});

		// One definition of the sheet's width, read back from the widths just assigned, so the header and
		// the admins block cannot disagree with the table below them.
		const totalColumns = worksheet.columns?.length ?? 1;

		let currentRow = renderSheetHeader({
			worksheet,
			title: sheetTitle,
			date: context.date,
			census: context.dayStats.census,
			hppd: context.dayStats.hppd,
			nursingHours: context.dayStats.totalHours,
			hideHppdNursingHours: settings.hideHppdNursingHours,
			censusPointInTimes: context.censusPointInTimes,
			totalColumns,
		});

		currentRow = renderAdminsBlock({
			worksheet,
			startRow: currentRow,
			config: settings.showAdmins,
			totalColumns,
		});

		for (const section of sheet.sections) {
			currentRow = renderSection({
				worksheet,
				section,
				currentRow,
				layout: sheet.layout,
				enabledColumns: settings.enabledColumns,
				rowGroupingBy: settings.rowGroupingBy,
				printoutGroupBy: settings.printoutGroupBy,
				hospitalData: context.hospitalData,
				department: context.department,
				timezone: context.timezone,
				jobManagementEnabled: context.jobManagementEnabled,
				printNotes: effectivePrintNotes,
				staffPerRow: settings.staffPerRow,
				showRoleGroupStats: settings.showRoleGroupStats,
				departmentLocationsRequired: context.departmentLocationsRequired,
				censusByLocationName: context.censusByLocationName,
				dayAbbr: context.dayAbbr,
				noteFlags,
				// `sortRowsByMergeColumns` runs inside the section renderer to cluster rows with
				// the same merge-column value (location, cert, etc.) adjacent — that's a
				// load-bearing precondition for `applyVerticalMerges` to collapse the cells into
				// one merged cell. The clustering would happen even with `sortBy: undefined`; we
				// pass `sortBy` through purely so the *direction* per merge column is honored
				// (e.g. `location desc` flips the cluster order). Easy to miss because Phase 2
				// already applied the user's full sort — but Phase 2's sort isn't enough on its
				// own; merge-column re-sort is what keeps the vertical merges stable.
				sortBy: settings.sortBy,
				customStringOrderMap: context.customStringOrderMap,
				mergeSimilarShiftTimes: settings.mergeSimilarShiftTimes,
				columnGroupingBy: settings.columnGroupingBy,
				appendRoleToName: settings.appendRoleToName,
				customColumnNames: settings.customColumnNames,
				hideColumnNames: settings.hideColumnNames,
				appendEmptyColumns: settings.appendEmptyColumns,
			});

			if (settings.spaceBetweenGroups) currentRow += 1;
		}

		// PTO section — suppressed under CUSTOM sheet split (mapping §7.3).
		// For other split modes, filter the global PTO list down to just the shifts that belong
		// on this sheet — otherwise the same PTO nurses (e.g. a 3p-11p shift) get rendered on
		// every tab regardless of the tab's window.
		if (ptoShifts.length > 0 && sheet.splitBy !== DailyPrintoutGroupingOption.CUSTOM) {
			const ptoShiftsForSheet = filterPtoForSheet(ptoShifts, sheet, context.jobManagementEnabled);
			if (ptoShiftsForSheet.length > 0) {
				// PTO never uses the pivot column expansion (which fans `name` across certification
				// columns) — it's a flat list. In pivot mode, pass the user's original
				// `enabledColumns` so the PTO table shows the chosen columns once each
				// (matches legacy `expandedColumns = columnGroupingBy ? enabledColumns : expandColumnsForStaffPerRow(...)`).
				const ptoExpandedColumns = sheet.layout.kind === "standard" ? sheet.layout.expandedColumns : settings.enabledColumns;
				currentRow = renderPtoSection({
					worksheet,
					currentRow,
					ptoShifts: ptoShiftsForSheet,
					hospitalData: context.hospitalData,
					department: context.department,
					timezone: context.timezone,
					jobManagementEnabled: context.jobManagementEnabled,
					rowGroupingBy: settings.rowGroupingBy,
					printoutGroupBy: settings.printoutGroupBy,
					staffPerRow: settings.staffPerRow,
					expandedColumns: ptoExpandedColumns,
					noteFlags,
					sortBy: settings.sortBy,
					customStringOrderMap: context.customStringOrderMap,
					appendRoleToName: settings.appendRoleToName,
					customColumnNames: settings.customColumnNames,
					hideColumnNames: settings.hideColumnNames,
				});
			}
		}

		// Call Outs section — never suppressed by split mode (see `filterCallOutsForSheet` doc).
		if (callOutShifts.length > 0) {
			const callOutsForSheet = filterCallOutsForSheet(callOutShifts, sheet, context.hospitalData, context.jobManagementEnabled);
			if (callOutsForSheet.length > 0) {
				currentRow = renderCallOutsSection({
					worksheet,
					currentRow,
					callOutShifts: callOutsForSheet,
					hospitalData: context.hospitalData,
					timezone: context.timezone,
					sortBy: settings.sortBy,
					lastNameFirst: context.lastNameFirst,
					hideColumnNames: settings.hideColumnNames,
				});
			}
		}
	}
};
